// ST-32 -Claude- Products list. BusinessOwner is scoped; NearrmeAdmin can view all businesses.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, EmptyState, LoadingSkeleton } from '@nearrme/ui-web';
import { isNearrmeAdminRole, listCompanies, listProducts } from '../api';
import { useAdminAuth } from '../useAdminAuth';
import { nextSort, SortHeader, sortRows, SortState } from '../tableSort';

export function AdminProductsPage() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const isNearrmeAdmin = isNearrmeAdminRole(user?.role);
  const [companyCode, setCompanyCode] = useState(isNearrmeAdmin ? '__all' : user?.companyCode ?? '');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState<'id' | 'business' | 'name' | 'brand' | 'category' | 'price' | 'stock'>>({
    key: 'name',
    direction: 'asc',
  });

  const companies = useQuery({ queryKey: ['admin', 'companies'], queryFn: listCompanies });
  const products = useQuery({
    queryKey: ['admin', 'products', companyCode],
    queryFn: () => listProducts(companyCode),
    enabled: !!companyCode,
  });

  const filtered = (products.data ?? []).filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (p.productName ?? '').toLowerCase().includes(q) ||
      (p.brandName ?? '').toLowerCase().includes(q) ||
      (p.productCategoryName ?? '').toLowerCase().includes(q) ||
      (p.companyCode ?? '').toLowerCase().includes(q)
    );
  });
  const sorted = sortRows(filtered, sort, {
    id: (p) => p.productId,
    business: (p) => p.companyCode,
    name: (p) => p.productName,
    brand: (p) => p.brandName,
    category: (p) => p.productCategoryName,
    price: (p) => p.salesPrice ?? p.defaultPrice,
    stock: (p) => p.availableQuantity ?? (p.inStock ? 1 : 0),
  });
  const sortHeader = (label: string, key: typeof sort.key) => (
    <SortHeader label={label} sortKey={key} sort={sort} onSort={(nextKey) => setSort((current) => nextSort(current, nextKey))} />
  );

  return (
    <div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">Products</h1>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
            {isNearrmeAdmin ? 'Network-wide catalog view.' : `Catalog scoped to ${user?.companyCode ?? 'your business'}.`}
          </p>
        </div>
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {isNearrmeAdmin ? 'Admin mode' : 'Business owner mode'}
        </div>
      </div>

      <Card padded className="mt-5 border border-slate-200 dark:border-slate-800 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Business</span>
            <select
              value={companyCode}
              onChange={(e) => setCompanyCode(e.target.value)}
              disabled={!isNearrmeAdmin}
              className="h-10 min-w-60 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500 disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:disabled:bg-slate-900"
            >
              {isNearrmeAdmin && <option value="__all">All businesses</option>}
              {(companies.data ?? []).map((c) => (
                <option key={c.companyCode ?? ''} value={c.companyCode ?? ''}>
                  {c.companyCode} - {c.companyName}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-72 flex-1">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">Filter</span>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, brand, category, or business code"
              className="h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
            />
          </label>
        </div>
      </Card>

      {!companyCode && (
        <Card padded className="mt-6">
          <div className="text-sm text-slate-600 dark:text-slate-400">Pick a business to load its products.</div>
        </Card>
      )}

      {products.isLoading && companyCode && (
        <div className="mt-6 space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <LoadingSkeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      )}

      {products.error && (
        <Card padded className="mt-6">
          <div className="text-red-600 dark:text-red-400 text-sm">{(products.error as Error).message}</div>
        </Card>
      )}

      {products.data && (
        <>
          <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            {filtered.length} {filtered.length === 1 ? 'product' : 'products'}
            {search && ` (filtered from ${products.data.length})`}
          </div>
          {filtered.length === 0 ? (
            <EmptyState title="No products" description={search ? 'No matches for your filter.' : `Business ${companyCode} has no active products.`} />
          ) : (
            <Card padded={false} className="mt-2 overflow-hidden border border-slate-200 dark:border-slate-800 shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
                    <tr className="text-left">
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('ID', 'id')}</th>
                      {isNearrmeAdmin && <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Business', 'business')}</th>}
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Name', 'name')}</th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Brand', 'brand')}</th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Category', 'category')}</th>
                      <th className="px-4 py-3 text-right text-slate-700 dark:text-slate-300">{sortHeader('Price', 'price')}</th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Stock', 'stock')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800 bg-white dark:bg-slate-950">
                    {sorted.map((p) => (
                      <tr
                        key={`${p.companyCode}-${p.productId}`}
                        onClick={() => navigate(`/admin/products/${p.productId}`)}
                        tabIndex={0}
                        role="link"
                        className="cursor-pointer hover:bg-slate-50 focus:bg-slate-50 focus:outline-none dark:hover:bg-slate-900 dark:focus:bg-slate-900"
                      >
                        <td className="px-4 py-3 font-mono text-xs text-slate-500 dark:text-slate-400">{p.productId}</td>
                        {isNearrmeAdmin && <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-400">{p.companyCode ?? '-'}</td>}
                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100">{p.productName ?? '-'}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{p.brandName ?? '-'}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{p.productCategoryName ?? '-'}</td>
                        <td className="px-4 py-3 text-right font-semibold text-slate-900 dark:text-slate-100">
                          {p.salesPrice != null ? `INR ${p.salesPrice.toLocaleString('en-IN')}` : '-'}
                        </td>
                        <td className="px-4 py-3">
                          {p.inStock ? (
                            <div>
                              <span className="rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-700 dark:bg-green-950 dark:text-green-400">
                                {p.availabilityStatus ?? 'Available'}
                              </span>
                              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                {p.availabilityComment ?? `${p.availableQuantity ?? 0} items in stock`}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700 dark:bg-red-950 dark:text-red-400">
                                {p.availabilityStatus ?? 'Out of Stock'}
                              </span>
                              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                {p.availabilityComment ?? 'Check back later'}
                              </div>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
